import React, { forwardRef, lazy, Suspense, useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react'
import ArrowUp from '@/assets/icons/arrowUp'
import ArrowDown from '@/assets/icons/arrowDown'
import QuestionFooter from '../question-footer'
import { formatDate, formatDistance, i18n } from '@/locale/i18n'
import questionService from '@/api/question'
import ClockIcon from '@/assets/icons/clock'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { selectHighlightedMarkets, selectMarketById, fetchOrderBook } from '@/store/markets'
import { useActiveCurrency, useCurrencies, useCurrentUser } from '@/hooks'
import useOrderBookRealTime from '@/hooks/useOrderBookRealTime'
import { keepQueryString, questionDetailUrl } from '@/urls'
import mixpanelService from '@/services/analytics/Mixpanel'
import { CLICK_COMPONENT, CLICK_ELEMENT, RENDER_MODE_SOURCE } from '@/constants/analyticsClick'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { compareDesc, parseISO } from 'date-fns'
import store from '@/store'
import {
  fetchUserOrders,
  getWagerSelected,
  selectSellingBets,
  selectUserOrders,
  selectWagerSelected,
  resetUserOrders,
} from '@/store/wagers'
// NOTE: Bet styles are imported by `Bet.jsx` itself, which is loaded lazily.
// Importing them here would pull the Bet CSS into the MarketCard chunk shared
// by the feed, even when no card has any wagers to display.
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  selectActiveLanguage,
  selectAllExchangeRate,
  selectOutcomeSelected,
  setOutcomeSelected,
  selectPageLayout,
} from '@/store/app'
import { ArrowRightTailed } from '@/assets/icons'
import QuestionHeader from '@/features/market/question-header'
import MarketCompactRow from '@/features/market/market-compact-row'
import SportEventMatchHeader from '@/features/market/question-header/SportEventMatchHeader'
import SportEventMobileMatchHeader from '@/features/market/sport-event/SportEventMobileMatchHeader'
import { createPortal } from 'react-dom'
import SportEventCompactRow from '@/features/market/sport-event/SportEventCompactRow'
import DockedPlaceBetSkeleton from '@/features/market/place-bet/DockedPlaceBetSkeleton'
import ErrorHandler from '@/components/ui/error-handler'
import constants from '@/constants'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import { deriveMarketUi, usesButtonsDisplay as usesButtonsDisplayUtil } from '../utils/marketType'
import { isSportsEvent, isSportsMatchLive, withSportsEventData } from '../utils/sportsEvent'
import { hasCryptoPriceTarget } from '../utils/cryptoEvent'
import { ACCORDION_MOTION } from '@/features/market/utils/accordionMotion'
import { leadingRowOutcomeFor } from '../outcome-list/outcomePricing'
import loadable from '@loadable/component'
import OverlayPanel from '@/components/OverlayPanel'
import Device from '@/util/Device'
import MarketShareFollow, {
  MarketFollowButton,
  MarketMoreMenu,
  MarketShareButton,
  useMarketFollow,
} from '@/features/market/share-follow'
// import ExternalIcon from "@/assets/icons/ExternalIcon";

import { adminPanelEditMarketUrl } from '@/urls'
import IconContainer from '@/components/ui/iconContainer/IconContainer'
import ErrorService from '@/services/errors/ErrorService'
import { BtcCurrencyBadge, BtcIcon, UsdcBadge, UsdtIcon } from '@/assets/icons/index'
import BetListLoadingShell from '../bet-list/BetListLoadingShell'
import OutcomeList from '@/features/market/outcome-list'

const Bet = loadable(() => import('@/pages/portfolio/components/bet/Bet'))
// const QuickBet = lazy(() => import('@/features/market/quick-bet'))
const QuickBet = loadable(() => import('@/features/market/quick-bet'))
const QuestionChart = lazy(() => import('@/features/market/question-chart/index.js'))
// `BetList` is only mounted in the market detail page (`inMarketPage === true`).
// Lazy-loading keeps it out of the feed's MarketCard chunk, where it would
// otherwise drag in `BetRow`, `TabbedContent`, transactions store slices, etc.
const BetList = lazy(() => import('../bet-list'))
// In-play soccer stats are detail-page-only and pull in world-cup hooks
const SportEventLiveStats = lazy(() => import('../sport-event/SportEventLiveStats'))

// Add shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('shimmer-styles')) {
  const style = document.createElement('style')
  style.id = 'shimmer-styles'
  style.textContent = shimmerStyles
  document.head.appendChild(style)
}

// Shimmer effect component
const ShimmerEffect = ({ className = '', children }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)',
        }}></div>
    </div>
  )
}

// Skeleton loader for Bet component
const BetSkeleton = () => {
  return (
    <ShimmerEffect className="p-4 bg-surface-2 border border-Neutral-2 rounded-lg">
      <div className="animate-pulse">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-surface-3 rounded-full"></div>
            <div className="h-4 bg-surface-3 rounded w-20"></div>
          </div>
          <div className="h-4 bg-surface-3 rounded w-16"></div>
        </div>
        <div className="flex justify-between items-center mb-2">
          <div className="h-5 bg-surface-3 rounded w-32"></div>
          <div className="h-6 bg-surface-3 rounded w-20"></div>
        </div>
        <div className="flex justify-between items-center">
          <div className="h-4 bg-surface-3 rounded w-24"></div>
          <div className="h-8 bg-surface-3 rounded w-16"></div>
        </div>
      </div>
    </ShimmerEffect>
  )
}

// Skeleton loader for QuickBet component
const QuickBetSkeleton = () => {
  return (
    <ShimmerEffect className="w-full bg-surface-1 rounded-b-lg p-4">
      <div className="animate-pulse space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="h-6 bg-surface-3 rounded w-24"></div>
          <div className="w-6 h-6 bg-surface-3 rounded"></div>
        </div>

        {/* Position selector */}
        <div className="flex gap-2">
          <div className="flex-1 h-10 bg-surface-3 rounded-lg"></div>
          <div className="flex-1 h-10 bg-surface-3 rounded-lg"></div>
        </div>

        {/* Amount input */}
        <div className="space-y-2">
          <div className="h-4 bg-surface-3 rounded w-16"></div>
          <div className="h-12 bg-surface-3 rounded-lg"></div>
        </div>

        {/* Potential return */}
        <div className="flex justify-between items-center py-2">
          <div className="h-4 bg-surface-3 rounded w-20"></div>
          <div className="h-4 bg-surface-3 rounded w-16"></div>
        </div>

        {/* Action button */}
        <div className="h-12 bg-surface-3 rounded-lg"></div>
      </div>
    </ShimmerEffect>
  )
}

// Skeleton loader for UnavailableMarket component
const UnavailableMarketSkeleton = () => {
  return (
    <ShimmerEffect className="p-6 rounded-lg bg-surface-2 border border-Neutral-2">
      <div className="animate-pulse flex flex-col items-center space-y-4">
        <div className="w-16 h-16 bg-surface-3 rounded-full"></div>
        <div className="space-y-2 text-center">
          <div className="h-5 bg-surface-3 rounded w-40 mx-auto"></div>
          <div className="h-4 bg-surface-3 rounded w-56 mx-auto"></div>
        </div>
        <div className="h-10 bg-surface-3 rounded w-32"></div>
      </div>
    </ShimmerEffect>
  )
}

// Skeleton loader for OutcomeList component
const OutcomeListSkeleton = ({ outcomeCount = 2 }) => {
  return (
    <div className="flex flex-col gap-2 w-full">
      {Array.from({ length: outcomeCount }).map((_, index) => (
        <ShimmerEffect key={index}>
          <div className="animate-pulse">
            <div className="flex justify-between items-center p-3 bg-surface-2 border border-Neutral-2 rounded-lg hover:bg-surface-3 transition-colors">
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 bg-surface-3 rounded-[var(--radius-xs)]"></div>
                <div className="space-y-1">
                  <div className="h-4 bg-surface-3 rounded w-20"></div>
                </div>
              </div>
              <div className="text-right space-y-1">
                <div className="h-4 bg-surface-3 rounded w-8"></div>
              </div>
            </div>
          </div>
        </ShimmerEffect>
      ))}
    </div>
  )
}

// Skeleton loader for QuestionHeader component
const QuestionHeaderSkeleton = () => {
  return (
    <ShimmerEffect className="space-y-3">
      <div className="animate-pulse">
        {/* Title */}
        <div className="space-y-2 mb-4">
          <div className="h-6 bg-surface-3 rounded w-full"></div>
          <div className="h-6 bg-surface-3 rounded w-3/4"></div>
        </div>
      </div>
    </ShimmerEffect>
  )
}

// Skeleton loader for QuestionFooter component
const QuestionFooterSkeleton = () => {
  return (
    <ShimmerEffect>
      <div className="animate-pulse">
        <div className="flex justify-between items-center py-3 border-t border-Neutral-2 !h-8">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-surface-3 rounded"></div>
            <div className="h-4 bg-surface-3 rounded w-20"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 bg-surface-3 rounded w-16"></div>
            <div className="w-4 h-4 bg-surface-3 rounded"></div>
          </div>
        </div>
      </div>
    </ShimmerEffect>
  )
}

// Chart loading fallback (matches Chart.jsx internal loading for consistent UX)
const ChartSkeleton = () => (
  <div className="flex flex-col justify-center items-center h-[253px] space-y-4">
    <div className="w-8 h-8 border-4 border-gray-200 border-t-primary-pure rounded-full animate-spin" />
    <span className="text-gray-500 text-sm">{i18n('common.loading_chart')}</span>
  </div>
)

// Comprehensive MarketCard skeleton (exported for feed loading state consistency)
export const MarketCardSkeleton = () => {
  return (
    <article className="question-card flex flex-col">
      {/* Categories outside the main card background */}
      <div className="mb-2">
        <div className="flex gap-2">
          <ShimmerEffect className="bg-surface-3 rounded-full">
            <div>
              <div className="h-4 bg-Neutral-3 opacity-50 rounded w-20"></div>
            </div>
          </ShimmerEffect>
          <ShimmerEffect className="bg-surface-3 rounded-full">
            <div>
              <div className="h-4 bg-Neutral-3 opacity-50 rounded w-16"></div>
            </div>
          </ShimmerEffect>
        </div>
      </div>

      {/* Main card with background */}
      <ShimmerEffect className="bg-surface-2 rounded-lg border border-surface-3 overflow-hidden flex-1">
        <div className="animate-pulse">
          <div className="p-4 space-y-4">
            <QuestionHeaderSkeleton />
            <OutcomeListSkeleton />
            <QuestionFooterSkeleton />
          </div>
        </div>
      </ShimmerEffect>
    </article>
  )
}

// Loading state for bet list
const BetListSkeleton = ({ betCount = 3 }) => {
  return (
    <div className="space-y-2">
      {Array.from({ length: betCount }).map((_, index) => (
        <BetSkeleton key={index} />
      ))}
    </div>
  )
}

const DYNAMIC_IMPORT_FETCH_FAILED = 'failed to fetch dynamically imported module'

// Error fallback for components
const ComponentErrorFallback = ({ componentName, onRetry, error }) => {
  // Track the error when component renders (reload on stale chunk after deploy)
  React.useEffect(() => {
    if (!error) return
    const msg = error?.message ?? ''
    if (msg.toLowerCase().includes(DYNAMIC_IMPORT_FETCH_FAILED)) {
      window.location.reload()
      return
    }
    ErrorService.captureError(error, {
      component: `${componentName}-MarketCard`,
      type: 'react_error',
      currentUrl: location.pathname,
    })
  }, [error, componentName])

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-surface-2 border border-dashed border-Neutral-3 rounded-lg mb-4">
      <div className="w-12 h-12 bg-negative-2 rounded-full flex items-center justify-center mb-3">
        <svg className="w-6 h-6 text-negative-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <div className="text-center space-y-2">
        <div className="text-Neutral-6 text-sm font-medium">
          {i18n('common.failed_to_load', { name: componentName })}
        </div>
        <div className="text-Neutral-4 text-xs">{error?.message || i18n('common.something_went_wrong')}</div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-3 px-4 py-2 text-xs bg-[var(--color-brand-04)] text-[var(--color-brand-01)] rounded-lg hover:bg-brand-05 transition-colors">
            Try Again
          </button>
        )}
      </div>
    </div>
  )
}

// Preload QuickBet component (only triggered on hover/intent, never on mount).
const preloadQuickBet = () => {
  QuickBet.preload()
}

// Store the clicked market card's position and dimensions for transition animation
const transitionStateData = {
  element: null,
  rect: null,
  scrollY: 0,
}

// Export a getter and setter for the transition state
export const getTransitionState = () => transitionStateData
export const setTransitionState = newState => {
  transitionStateData.element = newState.element
  transitionStateData.rect = newState.rect
  transitionStateData.scrollY = newState.scrollY
}

// Staff mode functions

const renderStaffModeData = question => {
  return (
    <div className="rounded-lg bg-gradient-to-r from-Neutral-6 to-Neutral-5 text-Neutral-1 py-3 px-4 mt-6 border border-Neutral-4">
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-4 items-center">
        {!!question.hot && (
          <span className=" text-lg" title={i18n('common.this_market_is_hot')}>
            🔥
          </span>
        )}
        {/* Relevance with hot indicator */}
        <div className="flex items-center gap-1 bg-yellow-100 border border-yellow-400 rounded-full px-2 py-1">
          <span className="text-yellow-300">⭐</span>
          <span className="text-sm font-semibold text-yellow-700">
            {question.relevance != null ? question.relevance : '-'}
          </span>
        </div>

        {/* Currency badge */}
        <div
          className={`flex items-center gap-2 rounded-full border px-3 py-1 ${
            question.canonical_currency === 'BTC'
              ? 'bg-orange-100 text-orange-800 border-orange-400'
              : question.canonical_currency === 'USDC'
                ? 'bg-blue-100 text-blue-800 border-blue-400'
                : 'bg-green-100 text-green-800 border-green-400'
          }`}>
          {question.canonical_currency === 'BTC' ? (
            <BtcCurrencyBadge width="1em" />
          ) : question.canonical_currency === 'USDC' ? (
            <UsdcBadge width="1em" />
          ) : (
            <UsdtIcon widht="1em" />
          )}
          <span className="text-sm font-semibold">{question.canonical_currency}</span>
        </div>

        {/* Orderbook status - most prominent */}
        <div
          className={`flex items-center gap-2 rounded-full px-3 py-1 ${
            question.order_book_enabled
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
          <span className="text-sm">📊</span>
          <span className="text-sm font-bold">{question.order_book_enabled ? 'ORDERBOOK' : 'NO ORDERBOOK'}</span>
        </div>

        {/* Admin edit link */}
        <a
          href={adminPanelEditMarketUrl(question.id)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 bg-surface-3 !text-Neutral-6 hover:bg-Neutral-3 rounded-xl px-3 py-1 transition-colors"
          title="Edit market in admin panel">
          <span className="text-sm">⚙️</span>
          <span className="text-sm font-semibold">EDIT</span>
        </a>
      </div>
    </div>
  )
}

const MarketCard = forwardRef(
  (
    {
      categories,
      showSelectedOutcome,
      isRelatedMarket = false,
      currentOutcomeSelection,
      position: globalPosition = null,
      onOutcomeSelected = () => {},
      showSummary,
      showChart = false,
      inlineBehavior,
      className = '',
      inMarketPage,
      noAnimation = false,
      setMarketSelected,
      id,
      isModalComponent = false,
      isSelectedToBet = false,
      forceBetListCollapsed = false,
      /** Controlled open state for the bet list. The list owns one open id so that
          exactly one row is ever expanded; leave undefined for the card's own state. */
      betListOpen,
      onToggleBetList = () => {},
      betListActiveTabIndex = null,
      betListTabRequestId = 0,
      onBetListTabChange = () => {},
      /** One-line row layout for the list view. Ignored on the market page, so no
          other surface can pick it up. Fixtures get the match row; every other
          market gets the generic one. */
      compactRow = false,
      /** Element the row's bet pad renders into — the list's column or sheet host. */
      quickBetPortalTarget = null,
      /** That host is a sheet, so the pad renders PlaceBet's sheet layout. */
      quickBetAsSheet = false,
      // Orderbook control props
      onOpenOrderbook = () => {},
      onScrollToOrderbook = () => {},
      onSelectOrderbookPrice = () => {},
      onPriceClick = () => {},
      onSetActiveTab = () => {},
      currentUser,
      renderAfterMarketCardSlot = null,
      onCardClick,
      onOutcomeClick,
      prioritizeLcpImage = false,
      ...props
    },
    externalRef
  ) => {
    const currentUserFromHook = useCurrentUser()
    const currencies = useCurrencies().currencies
    const effectiveCurrentUser = currentUser ?? currentUserFromHook
    const questionId = (props.question && props.question.id) || props.questionId
    const rawQuestion = props.question || useSelector(selectMarketById(questionId))
    const question = useMemo(() => withSportsEventData(rawQuestion), [rawQuestion])

    // Compute usesButtonsDisplay internally based on question
    const usesButtonsDisplay = useMemo(() => {
      return usesButtonsDisplayUtil(question)
    }, [
      question?.markets_correlation,
      question?.outcomes?.length,
      question?.outcomes?.[0]?.position_labels,
      question?.outcomes?.[1]?.position_labels,
    ])

    const marketUi = useMemo(() => deriveMarketUi(question), [
      question?.markets_correlation,
      question?.outcomes?.length,
      question?.outcomes?.[0]?.position_labels,
      question?.outcomes?.[1]?.position_labels,
    ])

    const [selectedOutcome, setSelectedOutcome] = useState()
    const chartRef = useRef()
    const quickBetActiveTabRef = useRef(null)
    const [position, setPosition] = useState(null)
    const [showQuickBet, setShowQuickBet] = useState(false)
    /* The list row's "view all": open, the odds column shows every outcome pair. */
    const [rowOutcomesExpanded, setRowOutcomesExpanded] = useState(false)
    const activeCurrency = useActiveCurrency()
    const isRealMoney = activeCurrency && activeCurrency?.id !== currencies.OOM.id
    const availableMarket = (question && !isRealMoney) || (isRealMoney && question.real_currency_available)
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const isMobileSize = useIsMobileSize()
    const [localBetListCollapsed, setIsBetListCollapsed] = useState(true)
    /* When the fixture row last took a click, so a quick second click reads as a
       double click on the row rather than a request for the market page. */
    const lastRowClickAtRef = useRef(0)
    const isBetListCollapsed = betListOpen === undefined ? localBetListCollapsed : !betListOpen
    const globalOutcomeSelected = useSelector(selectOutcomeSelected)
    const wagerSelected = useSelector(selectWagerSelected())
    const [showSellPlaceBet, setShowSellPlaceBet] = useState(false)
    const showBuyPlaceBet = globalOutcomeSelected && globalOutcomeSelected.id === selectedOutcome?.id
    /* The same condition `renderQuickBet` returns null on, so the card never hides its
       outcomes, footer and bet list for a pad that isn't there. */
    const isQuickBetActive = showQuickBet && (showBuyPlaceBet || showSellPlaceBet)
    const betList = question.wagers || props.betList || []
    /* A wager whose sale is still in flight is already gone as far as this card is
       concerned: leaving it in the open positions would offer it for sale a second
       time while the request runs. It comes back — with whatever shares are left —
       once the sale response has refreshed the market. */
    const sellingBets = useSelector(selectSellingBets())
    const isOpenBet = bet => bet.status === WAGER_STATUS.PURCHASE && !sellingBets[bet.id]
    const openBetCount = useMemo(
      () => betList.filter(isOpenBet).length,
      [betList, sellingBets]
    )
    const locale = useSelector(selectActiveLanguage()) 
    
    const highlightedMarkets = useSelector(selectHighlightedMarkets)
    const isHighlighted = highlightedMarkets.map(id => Number(id)).includes(Number(question?.id))
    const exchangeRateAll = useSelector(selectAllExchangeRate())
    const userOrders = useSelector(selectUserOrders()) || []
    const { adminMode } = useSelector(selectPageLayout)

    const fetchedOrderBooksRef = useRef(new Set())

    // Get unique outcome/currency/position combinations from active bets to fetch their order books
    const uniqueOutcomes = useMemo(() => {
      const outcomes = new Map()
      betList.forEach(bet => {
        const outcomeId = typeof bet.outcome === 'number' ? bet.outcome : bet.outcome?.id
        if (bet.status === WAGER_STATUS.PURCHASE && outcomeId && bet.position && bet.currency) {
          const key = `${outcomeId}_${bet.currency === 'OOM' ? 'OOM' : 'real'}_${bet.position}`
          if (!outcomes.has(key)) {
            outcomes.set(key, {
              outcomeId,
              currency: bet.currency,
              position: bet.position,
            })
          }
        }
      })
      return Array.from(outcomes.values())
    }, [betList])

    // Subscribe to real-time updates for each unique outcome/currency/position combination
    const orderBookSubscriptions = useMemo(() => {
      return uniqueOutcomes
        .map(({ outcomeId, currency, position }) => {
          return {
            marketId: question?.id,
            currency: currency,
            outcome: outcomeId,
            positionType: position,
          }
        })
        .filter(sub => sub.marketId && sub.currency && sub.outcome && sub.positionType)
    }, [uniqueOutcomes, question?.id])

    useOrderBookRealTime(inMarketPage ? orderBookSubscriptions : [])

    const fetchOrderBooksForActiveBets = () => {
      if (!question?.id || !uniqueOutcomes.length || !question?.order_book_enabled) return

      uniqueOutcomes.forEach(({ outcomeId, currency, position }) => {
        const fetchKey = `${question.id}_${outcomeId}_${currency}_${position}`
        if (fetchedOrderBooksRef.current.has(fetchKey)) return

        fetchedOrderBooksRef.current.add(fetchKey)
        dispatch(
          fetchOrderBook({
            question: question.id,
            currency_mode: currency === 'OOM' ? 'play_money' : 'real_money',
            outcome: outcomeId,
            position,
            source: 'market_card',
          })
        )
      })
    }

    // For now, let's just pass the original betList to avoid hooks violations
    // Order book calculations will be handled elsewhere or implemented differently
    const betsWithUpdatedAmounts = betList

    // Fetch user orders when component mounts
    useEffect(() => {
      if (inMarketPage && question.order_book_enabled && effectiveCurrentUser) {
        // Clear existing orders immediately when market changes to prevent showing old data
        dispatch(resetUserOrders()) 
        dispatch(
          fetchUserOrders({
            status: 'open',
            limit: 50,
            clear: true,
            question: question?.id,
          })
        )
      }
    }, [dispatch, inMarketPage, question?.id, effectiveCurrentUser])

    useEffect(() => {
      fetchedOrderBooksRef.current.clear()
    }, [question?.id])

    const compactRowLayout = compactRow && !inMarketPage
    /* Only a fixture has kickoff, sides and a score to lay out; anything else reads
       as title plus odds. */
    const isFixtureRow = compactRowLayout && isSportsEvent(question)
    /* The crypto price header stacks title, prices and difference (CryptoPriceQuestionHeader);
       it needs 12px more than the title header, taken from the slack around the buttons so
       the footer still ends with the card. */
    const isCryptoPriceCard = !compactRowLayout && !inMarketPage && hasCryptoPriceTarget(question)
    const feedOutcomesHeight = isCryptoPriceCard ? 'max-h-[118px] min-h-[64px] ' : 'max-h-[118px] min-h-[76px] '
    const isPortaledQuickBet = compactRowLayout && Boolean(quickBetPortalTarget)

    /* The outcome the row draws first, which is what clicking the row body picks. A
       fixture keeps the payload's order, which its own list reads as home/draw/away;
       every other market is drawn highest odds first. */
    const leadingRowOutcome = compactRowLayout
      ? leadingRowOutcomeFor(question, activeCurrency?.id, { isFixture: isFixtureRow })
      : null

    /* A row docked in the list's column shows its pad for one reason only: it owns the
       selection — the market picked and the outcome in the store are both its. A masonry
       relayout can remount the row and drop the pad that was showing it, and this puts
       it back. The row never picks for itself here, so it can never take a selection
       from another row; and an outcome that cannot take a bet stays closed. */
    useEffect(() => {
      if (!compactRowLayout || showQuickBet || !isSelectedToBet) return
      if (!isPortaledQuickBet || quickBetAsSheet) return
      const ownOutcome =
        globalOutcomeSelected && question?.outcomes?.find(outcome => outcome.id === globalOutcomeSelected.id)
      /* The row opens its pad whatever the market's state — closed, waiting, resolved —
         as its buttons do: the pad says why no bet can be placed, and the market page is
         the next click away. */
      if (!ownOutcome) return
      handleOutcomeSelected(ownOutcome, position ?? constants.position.LONG)
    }, [
      compactRowLayout,
      showQuickBet,
      isSelectedToBet,
      isPortaledQuickBet,
      quickBetAsSheet,
      question?.id,
      globalOutcomeSelected,
      position,
    ])

    /* The row layout owns the pad it opened above; once the card is back in the
       grid that state would otherwise reopen the pad inline. */
    useEffect(() => {
      if (compactRowLayout) return
      setRowOutcomesExpanded(false)
      setShowQuickBet(false)
      setShowSellPlaceBet(false)
      if (!selectedOutcome) return
      if (globalOutcomeSelected?.id === selectedOutcome.id) dispatch(setOutcomeSelected(null))
      setSelectedOutcome(null)
    }, [compactRowLayout])

    // Clear local selected outcome if global selection is from a different market
    useEffect(() => {
      if (globalOutcomeSelected && selectedOutcome && globalOutcomeSelected.id !== selectedOutcome.id) {
        setSelectedOutcome(null)
        setShowQuickBet(false)
      }
    }, [globalOutcomeSelected, selectedOutcome])

    // Set active tab to 'sell' when opening QuickBet for selling
    useEffect(() => {
      if (showSellPlaceBet && showQuickBet && quickBetActiveTabRef.current) {
        // Use setTimeout to ensure QuickBet/PlaceBet has mounted
        setTimeout(() => {
          if (quickBetActiveTabRef.current) {
            quickBetActiveTabRef.current('sell')
          }
        }, 100)
      }
    }, [showSellPlaceBet, showQuickBet, wagerSelected])

    useEffect(() => {
      if (currentOutcomeSelection && currentOutcomeSelection.id) {
        setSelectedOutcome(currentOutcomeSelection)
        dispatch(setOutcomeSelected(currentOutcomeSelection))
      }
    }, [currentOutcomeSelection])

    // useEffect(() => {
    //   if (wagerSelected && question) {
    //     const hasMatchingWager = question.wagers?.find(wager => wager.id === wagerSelected.id)
    //     setShowSellPlaceBet(!!hasMatchingWager)
    //     setShowQuickBet(true)

    //     // Update selectedOutcome to match the wager's outcome so the accordion updates
    //     if (wagerSelected.outcome?.id && inMarketPage) {
    //       const wagerOutcome = question.outcomes?.find(o => o.id === wagerSelected.outcome.id)
    //       if (wagerOutcome && (!selectedOutcome || selectedOutcome.id !== wagerOutcome.id)) {
    //         setSelectedOutcome(wagerOutcome)
    //         dispatch(setOutcomeSelected(wagerOutcome))
    //       }
    //     }
    //   } else {
    //     setShowSellPlaceBet(false)
    //   }
    // }, [wagerSelected, question, inMarketPage, selectedOutcome, dispatch])

    // Apply forced collapse state from parent component
    useEffect(() => {
      if (forceBetListCollapsed) {
        setIsBetListCollapsed(true)
      }
    }, [forceBetListCollapsed])

    const toggleBetListCollapse = () => {
      const newCollapsedState = !isBetListCollapsed
      setIsBetListCollapsed(newCollapsedState)
      // Notify parent component about the toggle
      onToggleBetList(newCollapsedState)
    }

    /* Selling the last open position puts the card back in its plain market layout,
       so an expanded bet list must not stay open under it. */
    useEffect(() => {
      if (inMarketPage || openBetCount > 0 || isBetListCollapsed) return
      setIsBetListCollapsed(true)
      onToggleBetList(true)
    }, [inMarketPage, openBetCount, isBetListCollapsed])

    // Preload Bet component on hover
    const handleCollapseButtonHover = () => {
      if (isBetListCollapsed && shouldShowBetStatus) {
        import('@/pages/portfolio/components/bet')
        fetchOrderBooksForActiveBets()
      }
    }

    // NOTE: We intentionally do NOT preload the `Bet` chunk on mount even when
    // `question.wagers` is present. The chunk is loaded by `loadable()` via
    // Suspense the moment the bet status section renders, and we additionally
    // preload it on the collapse-button hover (`handleCollapseButtonHover`).
    // Eagerly importing it for every visible card on the feed cost ~1 chunk
    // per card mount — see docs/PERFORMANCE_LCP.md.

    // Share/Follow functionality functions
    const toogleFollow = useMarketFollow(question, effectiveCurrentUser)

    const renderShareDropDown = () => <MarketShareButton question={question} />

    const renderFollowMarket = () => (
      <MarketFollowButton question={question} inMarketPage={inMarketPage} currentUser={effectiveCurrentUser} />
    )

    const renderShareFollow = () => (
      <MarketShareFollow question={question} inMarketPage={inMarketPage} currentUser={effectiveCurrentUser} />
    )

    const renderMoreMenu = () => <MarketMoreMenu question={question} currentUser={effectiveCurrentUser} />

    const goToQuestionDetails = () => {
      // Capture the current position and dimensions of the card for animation
      if (cardRef.current && !inMarketPage) {
        const rect = cardRef.current.getBoundingClientRect()
        setTransitionState({
          element: cardRef.current,
          rect: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
          scrollY: window.scrollY,
        })
      }

      navigate(questionDetailUrl(question), {
        state: {
          outcome: selectedOutcome,
          position,
          transition: true,
        },
      })
    }

    // Handle outcome selection within the card
    const handleOutcomeSelected = (outcome, pos) => {
      // Parent takes over outcome clicks entirely (e.g. WC lobby selects the
      // market for its bet rail instead of opening the inline quick bet)
      if (onOutcomeClick) {
        onOutcomeClick(outcome, pos, question)
        return
      }

      // Clear any previously selected outcome first
      if (globalOutcomeSelected) {
        dispatch(setOutcomeSelected(null))
      }

      setSelectedOutcome(outcome)
      dispatch(setOutcomeSelected(outcome))
      /* An odds click is a buy. Two things have to agree on that: the card's flag,
         or the effect that forces the Sell tab re-fires on the next wager sync; and
         PlaceBet's own tab, which the card can only reach through this ref. In the
         grid a closed pad unmounts and forgets its tab; the docked pad never
         unmounts, so a sale would leave it on Sell for every odds click after. */
      setShowSellPlaceBet(false)
      quickBetActiveTabRef.current?.('buy')

      // Handle position: support both old format ('yes'/'no') and new format (constants.position.LONG/SHORT)
      let normalizedPos = null
      if (pos === 'yes' || pos === 'no') {
        normalizedPos = pos === 'yes' ? constants.position.LONG : constants.position.SHORT
        setPosition(normalizedPos)
      } else if (pos === constants.position.LONG || pos === constants.position.SHORT) {
        normalizedPos = pos
        setPosition(normalizedPos)
      }

      setShowQuickBet(true)
      if (setMarketSelected) setMarketSelected(question)

      // Sync wager when outcome is selected
      if (outcome && question?.wagers) {
        // Keep the already selected wager only when it matches the clicked outcome and,
        // when a position was provided, the clicked position. This prevents the long (YES)
        // and short (NO) positions of the same outcome from overriding each other.
        const currentWager = selectWagerSelected()(store.getState())
        if (
          currentWager?.outcome?.id === outcome.id &&
          (!normalizedPos || currentWager.position === normalizedPos)
        ) {
          // Keep current wager; call parent and return
          onOutcomeSelected(outcome, pos)
          return
        }

        // Prefer the wager matching the clicked position, then the previously selected
        // currency/position, then the first available position for this outcome.
        const candidates = question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE && w.outcome?.id === outcome.id)
        const matchingWager =
          (normalizedPos && candidates.find(w => w.position === normalizedPos)) ||
          (currentWager && candidates.length > 1
            ? candidates.find(
                w => w.currency === currentWager.currency && w.position === currentWager.position
              ) || candidates[0]
            : candidates[0])

        if (matchingWager) {
          dispatch(getWagerSelected(matchingWager))
        } else {
          const firstWager = question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE)
          if (firstWager) {
            dispatch(getWagerSelected(firstWager))
          } else if (currentWager) {
            /* Nothing of this market's to select, so drop whatever the previous market
               left behind. Otherwise the pad opens on a wager from another question and
               its sell-side order book request pairs that wager's outcome with this
               question — which the API rejects. */
            dispatch(getWagerSelected(null))
          }
        }
      }

      // Call the parent's onOutcomeSelected if provided
      onOutcomeSelected(outcome, pos)
    }

    useEffect(() => {
      if (!inlineBehavior && selectedOutcome && !showQuickBet) goToQuestionDetails()
    }, [selectedOutcome, showQuickBet])

    /* Dynamically measure card width*/
    const cardRef = useRef(null)
    // Create a ref object that combines DOM element access and methods
    const marketCardRefWithMethods = useRef()

    // Expose chart refresh method via ref
    useImperativeHandle(
      externalRef,
      () => ({
        ...marketCardRefWithMethods.current, // Include any DOM methods if needed
        refreshChart: newOutcomes => {
          if (chartRef.current?.refreshChart) {
            chartRef.current.refreshChart(newOutcomes)
          }
        },
      }),
      []
    )

    // Combine external ref with internal ref for DOM access
    const combinedRef = ref => {
      cardRef.current = ref
      marketCardRefWithMethods.current = ref
    }

    const [cardWidth, setCardWidth] = useState(0)

    useEffect(() => {
      const updateCardWidth = () => {
        if (cardRef.current) {
          setCardWidth(cardRef.current.offsetWidth)
        }
      }

      const runAfterLayout = () => {
        requestAnimationFrame(updateCardWidth)
      }

      // Defer initial read to next frame to avoid forced reflow during paint
      runAfterLayout()
      window.addEventListener('resize', updateCardWidth)

      return () => window.removeEventListener('resize', updateCardWidth)
    }, [])

    const activeFirst = (acc, element) => {
      if (element.status === WAGER_STATUS.PURCHASE) {
        return [element, ...acc]
      }
      return [...acc, element]
    }
    /* Opens this card's pad on the Sell tab for one of its wagers. Shared by the
       grid card's single entry and the fixture row's positions table, so both
       surfaces sell the same way. */
    const openSellPad = wager => {
      if (!wager || wager.status === WAGER_STATUS.SALE) return
      dispatch(getWagerSelected({ ...wager, _diff: Math.random() }))
      if (setMarketSelected) setMarketSelected(question)

      let outcomeObj = wager.outcome
      if (typeof wager.outcome === 'number' && question?.outcomes) {
        outcomeObj = question.outcomes.find(o => o.id === wager.outcome)
      }
      if (!outcomeObj) return

      setSelectedOutcome(outcomeObj)
      dispatch(setOutcomeSelected(outcomeObj))
      setShowSellPlaceBet(true)
      setShowQuickBet(true)
    }

    const renderBetList = betList => {
      // Use the updated bet list with order book calculations
      const updatedBetList = betsWithUpdatedAmounts

      const openBetList = updatedBetList.filter(isOpenBet)
      const closedBetList = updatedBetList.filter(bet => bet.status !== WAGER_STATUS.PURCHASE)

      // Sort the open bets by last update date
      const sortedOpenBets = openBetList
        .sort((bet1, bet2) => compareDesc(new Date(bet1.last_update), new Date(bet2.last_update)))
        .reduce(activeFirst, [])

      // Sort the sold bets by last update date
      const sortedClosedBets = closedBetList
        .sort((bet1, bet2) => compareDesc(new Date(bet1.last_update), new Date(bet2.last_update)))
        .reduce(activeFirst, [])

      // Total number of bets
      const totalBets = sortedOpenBets.length

      // Check if there are sold or won bets to optimize column display
      const hasSoldBets = sortedClosedBets.some(bet => bet.status === WAGER_STATUS.SALE)
      const hasWonBets = sortedClosedBets.some(bet => bet.status === WAGER_STATUS.WIN)

      // Determine which bet to show when not in market page
      /* The grid card summarises the live position only: with nothing open left it
         shows no entry and the card reads as a plain market again. */
      const betToShow = sortedOpenBets.length > 0 ? sortedOpenBets[0] : null

      const betsToList = betToShow ? [betToShow] : []


      /* The fixture row expands into a full-width block, so it shows the same
         "My positions" table the market page does rather than the grid card's
         single-entry summary. */
      if (inMarketPage || compactRowLayout) {
        return (
          <ErrorHandler
            component="MarketCard.BetList"
            fallback={({ error, resetErrorBoundary }) => (
              <ComponentErrorFallback componentName="Bet List" error={error} onRetry={resetErrorBoundary} />
            )}>
            <Suspense
              fallback={
                <BetListLoadingShell
                  openCount={totalBets}
                  activeTabIndex={betListActiveTabIndex ?? 0}
                  orderBookEnabled={question.order_book_enabled}
                  rowCount={Math.max(totalBets, 1)}
                />
              }>
              <BetList
                question={question}
                sortedOpenBets={sortedOpenBets}
                sortedClosedBets={sortedClosedBets}
                totalBets={totalBets}
                /* Opt-in flag, not a claim about the route: it asks BetList for the
                   market-page treatment — the "My positions" heading and the active
                   table — which is what the row wants too. */
                inMarketPage={inMarketPage || compactRowLayout}
                collapsible={inMarketPage}
                activeOnly={compactRowLayout}
                setMarketSelected={setMarketSelected}
                externalActiveTabIndex={betListActiveTabIndex}
                externalTabRequestId={betListTabRequestId}
                onTabChange={onBetListTabChange}
                showSoldForColumn={hasSoldBets}
                showWonAmountColumn={hasWonBets}
                /* On the market page the parent owns the pad, so these bubble up. The
                   row owns its own pad: a Sell in its table opens this card's pad on
                   the Sell tab, exactly as the grid card's entry does. */
                onOutcomeSelected={
                  compactRowLayout ? (outcome, position, wager) => openSellPad(wager) : onOutcomeSelected
                }
                onSetActiveTab={onSetActiveTab}
              />
            </Suspense>
          </ErrorHandler>
        )
      }

      return (
        <>
          <div
            className={classNames('question-card__section--wide question-card__bet-status', {
              'rounded-b-lg !p-4': !inMarketPage,
            })}>
            {betsToList.map(entryBet => {
              const entryCurrencyReady = !entryBet?.currency || Boolean(currencies[entryBet.currency])
              return (
              <div key={`market--bet-${entryBet.id}`} className="question-card__bet-entry">
                <ErrorHandler
                  component="MarketCard.Bet"
                  fallback={({ error, resetErrorBoundary }) => (
                    <ComponentErrorFallback componentName="Bet" error={error} onRetry={resetErrorBoundary} />
                  )}>
                  <Suspense fallback={<BetSkeleton />}>
                    {entryCurrencyReady ? (
                      <Bet market={question} bet={entryBet} onSell={() => openSellPad(entryBet)} />
                    ) : (
                      <BetSkeleton />
                    )}
                  </Suspense>
                </ErrorHandler>
              </div>
              )
            })}

            {totalBets > betsToList.length && (
              <div>
                <Link
                  to={keepQueryString(questionDetailUrl(question))}
                  className="w-full font-bold flex justify-center link"
                  style={{ padding: '8px', width: '100%' }}>
                  <span>
                    {i18n('question_card.view_all')} ({totalBets})
                  </span>
                </Link>
              </div>
            )}
          </div>
        </>
      )
    }

    const renderQuestionHeader = () => {
      let questionSummary

      if (questionService.isWaitingForResults(question)) {
        questionSummary = renderWaitingForResultsMessage()
      }

      if (questionService.isCancelled(question)) {
        questionSummary = renderCancelledInfoMessage(question)
      }

      if (questionService.isResolved(question)) {
        questionSummary = renderResolvedQuestionLabel(question)
      }

      if (!questionSummary && questionService.hasUpcomingEvent(question)) {
        questionSummary = renderEventStartDateLabel(question)
      }

      // Footer in header for market-desktop, and for the mobile market page on a
      // sports fixture, where traders/volume share the row with the "more" button.
      const statsOnTitleRow = renderMode === 'market-mobile' && isSportsEvent(question)
      const footerInHeader = (config.showFooterInHeader || statsOnTitleRow) && (
        <div className="flex-shrink-0">
          <QuestionFooter
            question={question}
            inMarketPage={inMarketPage}
            hasBets={hasBets}
            isBetListCollapsed={isBetListCollapsed}
            toggleBetListCollapse={toggleBetListCollapse}
            onCollapseButtonHover={handleCollapseButtonHover}
            isMobileMarketPage={statsOnTitleRow}
            compact={statsOnTitleRow}
          />
        </div>
      )

      return (
        <ErrorHandler
          component="MarketCard.QuestionHeader"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Question Header" error={error} onRetry={resetErrorBoundary} />
          )}>
          <Suspense fallback={<QuestionHeaderSkeleton />}>
            <QuestionHeader
              showBrierScore={showSummary && question.complete}
              categories={categories}
              question={question}
              inMarketPage={inMarketPage}
              marketCardWidth={cardWidth}
              isRealMoney={isRealMoney}
              placebetOpened={showBuyPlaceBet || showSellPlaceBet}
              locale={locale}
              footer={footerInHeader}
              usesButtonsDisplay={usesButtonsDisplay}
              shareFollow={renderMode === 'market-mobile' ? renderMoreMenu() : renderMode === 'market-desktop' ? renderShareFollow() : null}
              prioritizeLcpImage={prioritizeLcpImage}
              leading={isCryptoPriceCard ? renderQuickBetBackButton() : null}
            />
          </Suspense>
        </ErrorHandler>
      )
    }

    const getBetForActiveCurrency = betList => betList.find(bet => bet.currency === activeCurrency?.id)

    const renderWaitingForResultsMessage = () => {
      return <div className="question-card__results-label">{i18n('question_card.waiting_result')}</div>
    }

    const renderCancelledInfoMessage = () => {
      return <div className="question-card__cancelled-label">{i18n('question_card.date.cancelled')}</div>
    }

    const renderResolvedQuestionLabel = question => {
      return (
        <div className="question-card__resolved-label">
          {formatDate(parseISO(question.resolve_date))}
          <br />
          <strong>{i18n('question_card.date.resolved')}</strong>
        </div>
      )
    }

    const renderEventStartDateLabel = question => {
      const startDateTS = Date.parse(question.event_start_date)
      return (
        <div className="question-card__start-date-label">
          <ClockIcon fill="currentColor" width="15px" /> {i18n('question_card.live_in')}{' '}
          {formatDistance(startDateTS, Date.now())}
        </div>
      )
    }

    const renderQuestionChart = () => {
      return (
        <ErrorHandler
          component="MarketCard.QuestionChart"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Question Chart" error={error} onRetry={resetErrorBoundary} />
          )}>
          <div
            className={classNames(
              'question-card__section question-card__chart question-card__section--gap-bottom justify-center',
              inMarketPage ? 'p-0' : 'pl-4 pr-0 pt-8 md:pl-6 md:pr-4',
              {
                'pb-4':
                  inMarketPage &&
                  isMobileSize &&
                  !marketUi.binaryLabelStyle,
              }
            )}>
            <Suspense fallback={<ChartSkeleton />}>
              <QuestionChart ref={chartRef} question={question} usesButtonsDisplay={usesButtonsDisplay} />
            </Suspense>
          </div>
        </ErrorHandler>
      )
    }

    const renderQuickBet = () => {
      if (inMarketPage) return null

      if (!showQuickBet || (!showBuyPlaceBet && !showSellPlaceBet)) {
        /* This row owns the column while it is the selected one, so between being picked
           and its pad being ready it stands the pad's frame up rather than leaving the
           column blank. Tied to the row that owes the pad, so it cannot outlive it, and
           returned plain because the caller below is what portals it. */
        return isPortaledQuickBet && !quickBetAsSheet && isSelectedToBet ? <DockedPlaceBetSkeleton /> : null
      }

      return (
        <div
          /* Attached under its card, the pad continues that card's surface. Docked in
             the column or risen as a sheet it is the panel itself — PlaceBet brings its
             own surface, radius and border, and a second one here would box it inside a
             box. */
          className={classNames('w-full overflow-visible market-card-quickbet', {
            'bg-surface-1 rounded-b-lg': !isPortaledQuickBet,
          })}>
          <ErrorHandler
            component="MarketCard.QuickBet"
            fallback={({ error, resetErrorBoundary }) => (
              <ComponentErrorFallback componentName="Quick Bet" error={error} onRetry={resetErrorBoundary} />
            )}>
            <Suspense
              fallback={
                isPortaledQuickBet && !quickBetAsSheet ? <DockedPlaceBetSkeleton /> : <QuickBetSkeleton />
              }>
              <QuickBet
                marketSelected={question}
                outcome={selectedOutcome}
                setOutcomeSelected={(outcome, pos) => {
                  setSelectedOutcome(outcome)
                  dispatch(setOutcomeSelected(outcome))
                  let normalizedPos = null
                  if (pos === 'yes' || pos === 'no') {
                    normalizedPos = pos === 'yes' ? constants.position.LONG : constants.position.SHORT
                    setPosition(normalizedPos)
                  } else if (pos === constants.position.LONG || pos === constants.position.SHORT) {
                    normalizedPos = pos
                    setPosition(normalizedPos)
                  }

                  // Sync wager when outcome is selected
                  if (outcome && question?.wagers) {
                    // Keep the already selected wager only when it matches the clicked outcome and,
                    // when a position was provided, the clicked position. This prevents the long (YES)
                    // and short (NO) positions of the same outcome from overriding each other.
                    const currentWager = selectWagerSelected()(store.getState())
                    if (
                      currentWager?.outcome?.id === outcome.id &&
                      (!normalizedPos || currentWager.position === normalizedPos)
                    ) {
                      // Keep current wager (e.g. from Sell click)
                    } else {
                      const candidates = question.wagers.filter(
                        w => w.status === WAGER_STATUS.PURCHASE && w.outcome?.id === outcome.id
                      )
                      const matchingWager =
                        (normalizedPos && candidates.find(w => w.position === normalizedPos)) ||
                        (currentWager && candidates.length > 1
                          ? candidates.find(
                              w =>
                                w.currency === currentWager.currency && w.position === currentWager.position
                            ) || candidates[0]
                          : candidates[0])

                      if (matchingWager) {
                        dispatch(getWagerSelected(matchingWager))
                      } else {
                        const firstWager = question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE)
                        if (firstWager) {
                          dispatch(getWagerSelected(firstWager))
                        } else if (currentWager) {
                          // Same as handleOutcomeSelected: never keep another market's wager.
                          dispatch(getWagerSelected(null))
                        }
                      }
                    }
                  }
                }}
                position={position}
                setPosition={setPosition}
                setMarketSelected={setMarketSelected}
                shouldShow={true}
                place="MarketCard"
                onClose={() => {
                  setShowQuickBet(false)
                  setShowSellPlaceBet(false)
                  dispatch(getWagerSelected(null))
                }}
                /* Docked in the list's column or drawer: no collapsed state, and it
                   keeps the market it just took a bet for instead of tearing the
                   selection down. */
                docked={isPortaledQuickBet}
                asSheet={isPortaledQuickBet && quickBetAsSheet}
                onOpenOrderbook={onOpenOrderbook}
                onScrollToOrderbook={onScrollToOrderbook}
                onSelectOrderbookPrice={onSelectOrderbookPrice}
                onSetActiveTabRef={quickBetActiveTabRef}
              />
            </Suspense>
          </ErrorHandler>
        </div>
      )
    }

    const renderOutcomeList = betList => {
      if (!question) return null

      /* In a list the lit button follows the store, which holds one outcome for the whole
         app, rather than this card's own memory of what it last aimed at. Every row keeps
         that memory, so on memory alone each row the reader passes through stays lit and
         the list ends up with several answers to "which one am I betting on". */
      const ownsSelection =
        Boolean(globalOutcomeSelected) &&
        (question?.outcomes ?? []).some(outcome => outcome.id === globalOutcomeSelected.id)
      const listSelectedOutcome = compactRowLayout ? (ownsSelection ? globalOutcomeSelected : null) : selectedOutcome

      /* On a row nothing hands the card a position — the market page does, the list does
         not — and it is the position, not the outcome, that tells the two sides of a
         binary market apart. So the row falls back to the one the card took when the bet
         was aimed, which is what its pad is working with. That memory outlives the
         selection just as the outcome's does, so it too counts only while the row owns
         the selection: a side lit by position alone is the same several-picks bug. */
      const listPosition = compactRowLayout ? (ownsSelection ? (globalPosition ?? position) : null) : globalPosition

      // OutcomeList returns null for button markets on desktop market page — skip wrapper/suspense
      if (usesButtonsDisplay && inMarketPage && !isMobileSize) {
        return null
      }

      const bet = getBetForActiveCurrency(betList)
      const outcomeClickable = availableMarket

      return (
        <ErrorHandler
          component="MarketCard.OutcomeList"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Outcome List" error={error} onRetry={resetErrorBoundary} />
          )}>
          <OutcomeList
            onSetActiveTab={onSetActiveTab}
            outcomes={question.outcomes}
            question={question}
            currencyId={activeCurrency?.id}
            selectedId={
              outcomeClickable && showSelectedOutcome && listSelectedOutcome
                ? // For Case 5 (non-linked unique custom), use virtualId format
                  (() => {
                    const isCase5 = marketUi.needsVirtualOutcomes && listPosition
                    return isCase5
                      ? `${listSelectedOutcome.id}_${listPosition === constants.position.LONG ? 'long' : 'short'}`
                      : listSelectedOutcome.id
                  })()
                : null
            }
            bet={bet}
            wager={bet && bet.outcome}
            onItemClick={handleOutcomeSelected}
            isRelatedMarket={isRelatedMarket}
            noAnimation={noAnimation}
            inMarketPage={inMarketPage}
            firstLabelIcon={
              question.outcomes?.[0]?.position_labels === 'updown' 
                ? <ArrowUp width="16" height="16" /> 
                : null
            }
            secondLabelIcon={
              question.outcomes?.[0]?.position_labels === 'updown' 
                ? <ArrowDown width="16" height="16" /> 
                : null
            }
            accordionMode={props.accordionMode || 'single'}
            onPriceClick={onPriceClick}
            currentOutcomeSelection={currentOutcomeSelection}
            globalPosition={listPosition}
            compact={compactRowLayout}
            compactExpanded={rowOutcomesExpanded}
            onToggleCompactExpanded={() => setRowOutcomesExpanded(expanded => !expanded)}
          />
        </ErrorHandler>
      )
    }

    const questionInfoClassList = classNames('question-card__section', 'question-card__main', className, {
      'rounded-t-lg': !inMarketPage,
      'rounded-b-lg': !inMarketPage && isBetListCollapsed && !isQuickBetActive,
      /* Fixture rows are sized by their content; the grid's fixed card height would
         leave most of each row empty. */
      'h-[238px] min-h-[238px]':
        !compactRowLayout && !inMarketPage && (!isQuickBetActive || isMobileSize),
    })
    /* Off the market page a card only ever shows live positions, so a sold-out
       market counts as having no bets. The market page keeps every wager — its
       list has open/closed tabs of its own. */
    const hasBets = inMarketPage ? betList.length > 0 : openBetCount > 0
    const shouldShowBetStatus = hasBets || (userOrders && userOrders.length > 0)

    if (question === null) {
      return <MarketCardSkeleton />
    }

    // Create article class list
    const articleClassList = classNames('question-card', {
      'question-card--show-bet-status ': shouldShowBetStatus,
      'space-y-4': inMarketPage && isMobileSize,
      'question-card--is-modal': isModalComponent,
      /* Names the list view's full-width row, which the list styles on hover. */
      'question-card--row': compactRowLayout,
      'question-card--is-selected': isSelectedToBet,
      /* A row is a line inside the list's own panel — the surface, the outline and the
         separators are the list's, so the row brings no card chrome of its own. */
      'bg-surface-2 rounded-lg': !inMarketPage && !compactRowLayout,
      'border border-solid border-surface-3': !inMarketPage && !isHighlighted && !compactRowLayout,
      /* The highlight is a card device: a solid border on one row would make it 2px
         taller than its neighbours and box it out of a list that has no boxes. */
      'border border-solid border-1 border-[var(--color-brand-05)]':
        isHighlighted && !inMarketPage && !compactRowLayout,
      /* The lift belongs to a card in a grid, where it reads as one tile coming forward.
         A full-width row has nothing to come forward from, and the whole line jumping
         under the pointer fights with reading it. */
      'hover:translate-y-[-4px]! transition-all duration-300': !inMarketPage && !compactRowLayout,
      'cursor-pointer': !inMarketPage,
    })

    // Determine the rendering mode
    const getRenderMode = () => {
      if (inMarketPage) {
        return isMobileSize ? 'market-mobile' : 'market-desktop'
      } else {
        return isMobileSize ? 'feed-mobile' : 'feed-desktop'
      }
    }

    const renderMode = getRenderMode()

    // `question.category` is the breadcrumb, root first.
    const marketClickProps = () => {
      const breadcrumb = question?.category || []
      return {
        source: RENDER_MODE_SOURCE[renderMode],
        market_id: question?.id,
        market_title: question?.title,
        category: breadcrumb[breadcrumb.length - 1]?.title,
        category_root: breadcrumb[0]?.title,
      }
    }
    const isBinaryQuestion = Boolean(marketUi.binaryLabelStyle) && (question?.outcomes?.length === 2)
    const isYesNoQuestion = marketUi.binaryLabelStyle === 'yesno' && (question?.outcomes?.length === 2)
    const isUpDownQuestion = marketUi.binaryLabelStyle === 'updown' && (question?.outcomes?.length === 2)

    // Mode-specific configuration
    const getModeConfig = mode => {
      /* The grid hides a card's outcomes, footer and bet list while its pad is open,
         because the pad sits inside the card. A fixture row's pad is portaled to the
         list's own surface, so the row keeps all three. */
      const quickBetTakesOverCard = isQuickBetActive && !isPortaledQuickBet
      const configs = {
        'feed-desktop': {
          showOutcomeList: !quickBetTakesOverCard,
          showFooterInHeader: false,
          showFooterSeparate: !quickBetTakesOverCard,
          showBetStatus: shouldShowBetStatus && !quickBetTakesOverCard && !isBetListCollapsed,
          outcomeListPosition: 'bottom',
          footerPosition: 'bottom',
        },
        'feed-mobile': {
          showOutcomeList: true,
          showFooterInHeader: false,
          showFooterSeparate: true,
          showBetStatus: shouldShowBetStatus && !isBetListCollapsed,
          outcomeListPosition: 'header',
          footerPosition: 'separate',
        },
        'market-desktop': {
          showOutcomeList: !isBinaryQuestion || usesButtonsDisplay,
          showFooterInHeader: true,
          showFooterSeparate: false,
          showBetStatus: shouldShowBetStatus,
          outcomeListPosition: 'bottom',
          footerPosition: 'header',
        },
        'market-mobile': {
          showOutcomeList: true,
          showFooterInHeader: false,
          showFooterSeparate: true,
          showBetStatus: shouldShowBetStatus,
          outcomeListPosition: 'header',
          footerPosition: 'separate',
        },
      }
      return configs[mode] || configs['feed-desktop']
    }

    const config = getModeConfig(renderMode)

    const renderQuickBetBackButton = () =>
      renderMode === 'feed-desktop' &&
      isQuickBetActive && (
        <button
          className="shrink-0"
          onClick={() => {
            setShowQuickBet(false)
            dispatch(setOutcomeSelected(null))
            setSelectedOutcome(null)
            dispatch(getWagerSelected(null))
            setShowSellPlaceBet(false)
          }}>
          <ArrowRightTailed className="rotate-180 cursor-pointer !text-Neutral-3" width="16px" height="16px" />
        </button>
      )

    // Layout-specific render functions
    const renderHeader = () => (
      <div
        className={
          'question-card__header-row flex items-start gap-2 ' +
          (!inMarketPage ? (isCryptoPriceCard ? (renderMode.includes('mobile') ? '!max-h-[126px] ' : '!max-h-[114px] ') : '!max-h-[98px] ') : '') +
          (renderMode === 'market-mobile'
            ? 'px-0 pt-5 pb-2'
            : renderMode.includes('mobile')
              ? 'px-4 pt-5 pb-2'
              : inMarketPage
                ? ' px-6 py-6 '
                : ' px-4 pt-4 ')
        }>
        {!isCryptoPriceCard && renderQuickBetBackButton()}
        {renderQuestionHeader()}
      </div>
    )

    const renderOutcomeSection = () => {
      if (!config.showOutcomeList) return null

      const outcomeContent = renderOutcomeList(betList)
      
      // Don't render wrapper div if outcomeContent is null
      if (outcomeContent === null) return null
      
      // Desktop market page runs the outcome list edge to edge.
      const isDesktopMarketPage = inMarketPage && !isMobileSize

      if (config.outcomeListPosition === 'header') {
        // Binary yes/no markets put their buttons in a bottom-sticky bar on the mobile market
        // page, so the in-flow wrapper would contribute nothing but empty space.
        if (renderMode === 'market-mobile' && usesButtonsDisplay) {
          return outcomeContent
        }

        return (
          <div
            className={
              'question-card__outcomes ' +
              (inMarketPage && renderMode === 'market-mobile' && (isYesNoQuestion || isUpDownQuestion)
                ? ''
                : `flex-grow overflow-auto flex ${inMarketPage ? '' : 'px-[20px]'} `) +
              (renderMode === 'market-mobile' && !isYesNoQuestion && !isUpDownQuestion ? 'pb-4 pt-8' : '') +
              (!inMarketPage ? feedOutcomesHeight : '')
            }>
            {outcomeContent}
          </div>
        )
      }

      if (config.outcomeListPosition === 'bottom') {
        return (
          <div
            className={
              'question-card__outcomes ' +
              `flex-grow overflow-auto flex ${isDesktopMarketPage ? 'py-4' : 'p-4'} ` +
              (!inMarketPage ? 'py-0 ' + feedOutcomesHeight : '')
            }>
            {outcomeContent}
          </div>
        )
      }

      return null
    }

    // Soccer matches on the detail page: WC-style match header, full card width.
    // Desktop renders it inside MarketPageHeader instead, above the stats row.
    // The mobile detail page leads with a sticky scoreboard that compacts on scroll.
    const renderSportEventMatchHeader = () => {
      if (!inMarketPage || !isSportsEvent(question)) return null
      if (renderMode === 'market-desktop') return null

      if (renderMode === 'market-mobile') {
        return <SportEventMobileMatchHeader question={question} categories={categories} />
      }

      return (
        <div className="py-6 md:px-6">
          <SportEventMatchHeader question={question} categories={categories} />
        </div>
      )
    }

    const renderFooterSection = () => {
      if (!config.showFooterSeparate) return null
      // Mobile fixtures render it on the title row instead (see footerInHeader).
      if (renderMode === 'market-mobile' && isSportsEvent(question)) return null

      const footerProps = {
        question,
        inMarketPage,
        hasBets,
        isBetListCollapsed,
        toggleBetListCollapse,
        onCollapseButtonHover: handleCollapseButtonHover,
        isMobileMarketPage: renderMode === 'market-mobile',
        compact: inMarketPage && isSportsEvent(question),
      }

      // Add mode-specific props
      if (renderMode === 'feed-mobile') {
        footerProps.renderShareFollow = renderShareFollow
        footerProps.renderFollowMarket = renderFollowMarket
      } else if (renderMode === 'feed-desktop') {
        footerProps.renderFollowMarket = renderFollowMarket
        footerProps.toogleFollow = toogleFollow
      }
      // market-mobile keeps share/follow on the title row instead of the footer

      return (
        <div
          className={
            'question-card__footer flex-shrink-0 flex items-center ' +
            (renderMode === 'market-mobile' ? 'px-0' : 'px-4 h-[48px] md:h-[48px]!')
          }>
          <QuestionFooter {...footerProps} />
        </div>
      )
    }

    const renderBetStatusSection = () => {
      if (!config.showBetStatus) return null

      return (
        <ErrorHandler
          component="MarketCard.BetStatus"
          fallback={({ error, resetErrorBoundary }) => (
            <ComponentErrorFallback componentName="Bet Status" error={error} onRetry={resetErrorBoundary} />
          )}>
          <Suspense fallback={<BetListSkeleton betCount={betList.length || 3} />}>{renderBetList(betList)}</Suspense>
        </ErrorHandler>
      )
    }

    /* The row opens its positions as an accordion; a card in the grid keeps the
       library's own timing, which is what its other transitions were tuned against. */
    const [animationParent] = useAutoAnimate(compactRowLayout ? ACCORDION_MOTION : undefined)

    // Check if the clicked element is an interactive element
    const isInteractiveElement = (element, cardRoot) => {
      if (!element) return false

      // Check if element itself is interactive
      const tagName = element.tagName?.toLowerCase()
      const interactiveTags = ['button', 'a', 'input', 'select', 'textarea', 'label']
      if (interactiveTags.includes(tagName)) return true

      // Check for role="button" or role="link"
      const role = element.getAttribute('role')
      if (role === 'button' || role === 'link') return true

      // Check if element has onClick handler
      if (element.onclick) return true

      // Ignore the card root itself — only nested descendants should block the card's own onClick
      const closestInteractive = element.closest('button, a, [role="button"], [role="link"], input, select, textarea')
      if (closestInteractive && closestInteractive !== cardRoot) return true

      return false
    }

    const handleCardClick = e => {
      // Don't navigate if clicking on an interactive element or its child
      // Walk up the DOM tree to check if click originated from an interactive element
      let currentElement = e.target
      while (currentElement && currentElement !== e.currentTarget) {
        if (isInteractiveElement(currentElement, e.currentTarget)) {
          return
        }
        currentElement = currentElement.parentElement
      }

      mixpanelService.trackClick({
        component: CLICK_COMPONENT.MARKET_CARD,
        element: CLICK_ELEMENT.CARD_BODY,
        // `onCardClick` callers handle the click themselves and may not navigate.
        ...(onCardClick ? {} : { destination: questionDetailUrl(question) }),
        ...marketClickProps(),
      })

      if (onCardClick) {
        onCardClick(question, e)
        return
      }

      /* A fixture row is a bet control first. Clicking it aims the bet pad at this
         match — on the first outcome unless the user already picked one of this
         match's outcomes, which stays theirs — and, with open wagers on the match,
         also expands or collapses the bet list under the row (the same one its
         bet-count button toggles). A row that already has the focus opens the
         market page on the next click. */
      if (compactRowLayout) {
        const now = Date.now()
        const isDoubleClick = now - lastRowClickAtRef.current < 300
        lastRowClickAtRef.current = now
        if (isDoubleClick) return

        if (isSelectedToBet && showQuickBet) {
          navigate(questionDetailUrl(question))
          return
        }

        const outcomes = question?.outcomes || []
        const selectionIsOurs =
          globalOutcomeSelected && outcomes.some(outcome => outcome.id === globalOutcomeSelected.id)
        if (!selectionIsOurs && leadingRowOutcome)
          handleOutcomeSelected(leadingRowOutcome, constants.position.LONG)
        else if (selectionIsOurs && !showQuickBet) setShowQuickBet(true)

        const hasOpenWagers = (question?.wagers || []).some(wager => wager.status === WAGER_STATUS.PURCHASE)
        if (hasOpenWagers || !isBetListCollapsed) toggleBetListCollapse()
        return
      }

      navigate(questionDetailUrl(question))
    }

    return (
      <article
        className={`${articleClassList} flex flex-col`}
        ref={animationParent}
        onMouseEnter={preloadQuickBet}
        onPointerDown={preloadQuickBet}
        // Mouse/touch only: the card contains real focusable descendants (the
        // question title link, outcome buttons, footer actions), so it isn't
        // also exposed as role="button"/tabIndex — that would nest interactive
        // controls inside one another, which axe flags and screen readers
        // announce badly. Keyboard users reach the same destination through
        // the title link (see QuestionHeader) or the inner buttons directly.
        onClick={handleCardClick}>
        {/* Desktop market page renders categories and the header at page level so they
            can span the full grid width — see MarketPageHeader. */}

        {compactRowLayout ? (
          <div className={`${questionInfoClassList} flex flex-col`} id={id} ref={combinedRef}>
            {isFixtureRow ? (
              <SportEventCompactRow
                question={question}
                outcomes={renderOutcomeList(betList)}
                isRealMoney={isRealMoney}
                betListOpen={!isBetListCollapsed}
                onToggleBetList={toggleBetListCollapse}
              />
            ) : (
              <MarketCompactRow
                question={question}
                outcomes={renderOutcomeList(betList)}
                isRealMoney={isRealMoney}
                betListOpen={!isBetListCollapsed}
                onToggleBetList={toggleBetListCollapse}
              />
            )}
          </div>
        ) : (
        <div className={`${questionInfoClassList} flex flex-col`} id={id} ref={combinedRef}>
          <div
            className={classNames('flex-shrink-0 mb-4 ', {
              '!flex !flex-col !h-full': renderMode === 'feed-mobile' && config.outcomeListPosition === 'header',
            })}>
            {/* Mobile market page leads with the sticky scoreboard; the stats row
                (traders / volume) follows on the title row. */}
            {renderMode === 'market-mobile' && renderSportEventMatchHeader()}
            {renderMode !== 'market-desktop' && renderHeader()}
            {renderMode !== 'market-mobile' && renderSportEventMatchHeader()}
            {/* Show outcomes in header position for most cases */}
            {config.outcomeListPosition === 'header' &&
              !(
                inMarketPage &&
                isMobileSize &&
                !marketUi.binaryLabelStyle
              ) &&
              renderOutcomeSection()}
            {(renderMode === 'feed-mobile' || renderMode === 'market-mobile') && renderFooterSection()}
            {showChart && availableMarket && renderQuestionChart()}
            {/* Mobile only — on desktop the live stats render in the page sidebar under the bet pad */}
            {inMarketPage && isMobileSize && isSportsEvent(question) && isSportsMatchLive(question) && (
              <Suspense fallback={null}>
                <SportEventLiveStats question={question} />
              </Suspense>
            )}
            {/* Show outcomes under chart only for: market page + mobile + non-binary markets */}
            {inMarketPage &&
              isMobileSize &&
              !marketUi.binaryLabelStyle &&
              renderOutcomeSection()}
          </div>

          {/* Show outcomes in bottom position for cases that don't meet the special condition */}
          {config.outcomeListPosition === 'bottom' &&
            !(
              inMarketPage &&
              isMobileSize &&
              !marketUi.binaryLabelStyle
            ) &&
            renderOutcomeSection()}
          {renderMode === 'feed-desktop' && renderFooterSection()}
        </div>
        )}

        {renderAfterMarketCardSlot}
        {renderBetStatusSection()}
        {/* In the list the bet pad belongs to the layout's right-hand column, not
            inside the row. Portalling keeps this card's existing wiring — outcome
            selection, wager sync, sell flow — and only moves where it lands. */}
        {isPortaledQuickBet ? createPortal(renderQuickBet(), quickBetPortalTarget) : renderQuickBet()}

        {/* Staff mode data */}
        {effectiveCurrentUser && adminMode && renderStaffModeData(question)}
      </article>
    )
  }
)
export default MarketCard
